/**
 * Best-effort LaTeX → Unicode decoder.
 *
 * Takes a LaTeX source string and emits a paste-anywhere Unicode-only equivalent.
 * Covers ~95% of the operators / Greek / sub-super / fractions / radicals / common
 * physics structures that show up in real-world LaTeX. Anything it can't downgrade
 * (e.g. matrices, complex multi-line align blocks) is left visible as `[latex: ...]`
 * placeholders so the user knows that part needs manual cleanup.
 */

// Greek letter map — both lowercase and uppercase commands
const GREEK = {
  alpha: "α", beta: "β", gamma: "γ", delta: "δ", epsilon: "ε", varepsilon: "ϵ",
  zeta: "ζ", eta: "η", theta: "θ", vartheta: "ϑ", iota: "ι", kappa: "κ",
  varkappa: "ϰ", lambda: "λ", mu: "μ", nu: "ν", xi: "ξ", omicron: "ο", pi: "π",
  varpi: "ϖ", rho: "ρ", varrho: "ϱ", sigma: "σ", varsigma: "ς", tau: "τ",
  upsilon: "υ", phi: "φ", varphi: "ϕ", chi: "χ", psi: "ψ", omega: "ω",
  Alpha: "Α", Beta: "Β", Gamma: "Γ", Delta: "Δ", Epsilon: "Ε", Zeta: "Ζ",
  Eta: "Η", Theta: "Θ", Iota: "Ι", Kappa: "Κ", Lambda: "Λ", Mu: "Μ", Nu: "Ν",
  Xi: "Ξ", Omicron: "Ο", Pi: "Π", Rho: "Ρ", Sigma: "Σ", Tau: "Τ", Upsilon: "Υ",
  Phi: "Φ", Chi: "Χ", Psi: "Ψ", Omega: "Ω",
};

// Common operators / symbols
const SYMBOL = {
  // Single-letter blackboard shortcuts that some authors use as macros
  R: "ℝ", N: "ℕ", Z: "ℤ", Q: "ℚ", C: "ℂ", H: "ℍ", P: "ℙ", F: "𝔽", S: "𝕊", E: "𝔼",
  // Calculus + analysis
  partial: "∂", nabla: "∇", infty: "∞", aleph: "ℵ", hbar: "ℏ", ell: "ℓ",
  sum: "∑", prod: "∏", coprod: "∐", int: "∫", iint: "∬", iiint: "∭",
  oint: "∮", oiint: "∯", lim: "lim", min: "min", max: "max", inf: "inf",
  sup: "sup", arg: "arg", det: "det", deg: "deg", dim: "dim", ker: "ker",
  log: "log", ln: "ln", exp: "exp", sin: "sin", cos: "cos", tan: "tan",
  cot: "cot", sec: "sec", csc: "csc", sinh: "sinh", cosh: "cosh", tanh: "tanh",
  // Relations
  le: "≤", leq: "≤", ge: "≥", geq: "≥", ne: "≠", neq: "≠", equiv: "≡",
  approx: "≈", sim: "∼", simeq: "≃", cong: "≅", propto: "∝",
  ll: "≪", gg: "≫", prec: "≺", succ: "≻", preceq: "≼", succeq: "≽",
  // Set theory
  in: "∈", notin: "∉", ni: "∋", subset: "⊂", supset: "⊃",
  subseteq: "⊆", supseteq: "⊇", cup: "∪", cap: "∩", setminus: "∖",
  emptyset: "∅", forall: "∀", exists: "∃", nexists: "∄",
  // Logic + algebra
  land: "∧", wedge: "∧", lor: "∨", vee: "∨", neg: "¬", lnot: "¬",
  oplus: "⊕", otimes: "⊗", ominus: "⊖", odot: "⊙",
  cdot: "·", times: "×", div: "÷", ast: "∗", star: "⋆", circ: "∘",
  pm: "±", mp: "∓",
  // Arrows
  to: "→", rightarrow: "→", leftarrow: "←", leftrightarrow: "↔",
  Rightarrow: "⇒", Leftarrow: "⇐", Leftrightarrow: "⇔",
  mapsto: "↦", longrightarrow: "⟶", longleftarrow: "⟵", uparrow: "↑", downarrow: "↓",
  // Misc
  therefore: "∴", because: "∵", angle: "∠", perp: "⊥", parallel: "∥",
  Box: "□", square: "□", diamond: "◇", triangle: "△", surd: "√",
  ldots: "…", cdots: "⋯", vdots: "⋮", ddots: "⋱",
  // Number sets (mathbb done separately, but bare commands too)
  // Blackboard bold
  Re: "ℜ", Im: "ℑ", wp: "℘", mho: "℧",
};

// Unicode superscripts/subscripts — only some characters exist as combining ranges
const SUP = {
  "0": "⁰","1": "¹","2": "²","3": "³","4": "⁴","5": "⁵","6": "⁶","7": "⁷","8": "⁸","9": "⁹",
  "+": "⁺","-": "⁻","−": "⁻","=": "⁼","(": "⁽",")": "⁾",
  "a": "ᵃ","b": "ᵇ","c": "ᶜ","d": "ᵈ","e": "ᵉ","f": "ᶠ","g": "ᵍ","h": "ʰ","i": "ⁱ","j": "ʲ",
  "k": "ᵏ","l": "ˡ","m": "ᵐ","n": "ⁿ","o": "ᵒ","p": "ᵖ","r": "ʳ","s": "ˢ","t": "ᵗ","u": "ᵘ",
  "v": "ᵛ","w": "ʷ","x": "ˣ","y": "ʸ","z": "ᶻ",
  // Greek superscripts that exist in Unicode
  "α": "ᵅ","β": "ᵝ","γ": "ᵞ","δ": "ᵟ","ε": "ᵋ","θ": "ᶿ","ι": "ᶥ","φ": "ᵠ","χ": "ᵡ",
};
const SUB = {
  "0": "₀","1": "₁","2": "₂","3": "₃","4": "₄","5": "₅","6": "₆","7": "₇","8": "₈","9": "₉",
  "+": "₊","-": "₋","−": "₋","=": "₌","(": "₍",")": "₎",
  "a": "ₐ","e": "ₑ","h": "ₕ","i": "ᵢ","j": "ⱼ","k": "ₖ","l": "ₗ","m": "ₘ","n": "ₙ","o": "ₒ",
  "p": "ₚ","r": "ᵣ","s": "ₛ","t": "ₜ","u": "ᵤ","v": "ᵥ","x": "ₓ",
  // Greek subscripts that exist in Unicode
  "β": "ᵦ","γ": "ᵧ","ρ": "ᵨ","φ": "ᵩ","χ": "ᵪ",
};

// Try to convert a string into its sub/super unicode form. Returns null if any char
// has no mapping (caller can fall back to `_(x)` / `^(x)` style).
const tryUnicodeRun = (s, table) => {
  let out = "";
  for (const ch of s) {
    if (!Object.prototype.hasOwnProperty.call(table, ch)) return null;
    out += table[ch];
  }
  return out;
};

// Convert \mathbb{X} / \mathcal{X} / \mathfrak{X} to single-char Unicode equivalents.
// Covers BOTH upper and lower case so users get 𝕥 (double-lined t), 𝓇 (script r), etc.
const MATHBB = {
  // Uppercase
  N: "ℕ", Z: "ℤ", Q: "ℚ", R: "ℝ", C: "ℂ", H: "ℍ", P: "ℙ", E: "𝔼", F: "𝔽", S: "𝕊",
  A: "𝔸", B: "𝔹", D: "𝔻", G: "𝔾", I: "𝕀", J: "𝕁", K: "𝕂", L: "𝕃", M: "𝕄",
  O: "𝕆", T: "𝕋", U: "𝕌", V: "𝕍", W: "𝕎", X: "𝕏", Y: "𝕐",
  // Lowercase — double-lined (blackboard bold) lowercase letters
  a: "𝕒", b: "𝕓", c: "𝕔", d: "𝕕", e: "𝕖", f: "𝕗", g: "𝕘", h: "𝕙", i: "𝕚", j: "𝕛",
  k: "𝕜", l: "𝕝", m: "𝕞", n: "𝕟", o: "𝕠", p: "𝕡", q: "𝕢", r: "𝕣", s: "𝕤", t: "𝕥",
  u: "𝕦", v: "𝕧", w: "𝕨", x: "𝕩", y: "𝕪", z: "𝕫",
  // Double-struck digits (free win)
  "0": "𝟘", "1": "𝟙", "2": "𝟚", "3": "𝟛", "4": "𝟜", "5": "𝟝", "6": "𝟞", "7": "𝟟", "8": "𝟠", "9": "𝟡",
};
const MATHCAL = {
  // Uppercase — script / calligraphic
  A: "𝒜", B: "ℬ", C: "𝒞", D: "𝒟", E: "ℰ", F: "ℱ", G: "𝒢", H: "ℋ", I: "ℐ", J: "𝒥",
  K: "𝒦", L: "ℒ", M: "ℳ", N: "𝒩", O: "𝒪", P: "𝒫", Q: "𝒬", R: "ℛ", S: "𝒮", T: "𝒯",
  U: "𝒰", V: "𝒱", W: "𝒲", X: "𝒳", Y: "𝒴", Z: "𝒵",
  // Lowercase — script lowercase
  a: "𝒶", b: "𝒷", c: "𝒸", d: "𝒹", e: "ℯ", f: "𝒻", g: "ℊ", h: "𝒽", i: "𝒾", j: "𝒿",
  k: "𝓀", l: "ℓ", m: "𝓂", n: "𝓃", o: "ℴ", p: "𝓅", q: "𝓆", r: "𝓇", s: "𝓈", t: "𝓉",
  u: "𝓊", v: "𝓋", w: "𝓌", x: "𝓍", y: "𝓎", z: "𝓏",
};
const MATHFRAK = {
  // Uppercase fraktur
  A: "𝔄", B: "𝔅", C: "ℭ", D: "𝔇", E: "𝔈", F: "𝔉", G: "𝔊", H: "ℌ", I: "ℑ", J: "𝔍",
  K: "𝔎", L: "𝔏", M: "𝔐", N: "𝔑", O: "𝔒", P: "𝔓", Q: "𝔔", R: "ℜ", S: "𝔖", T: "𝔗",
  U: "𝔘", V: "𝔙", W: "𝔚", X: "𝔛", Y: "𝔜", Z: "ℨ",
  // Lowercase fraktur
  a: "𝔞", b: "𝔟", c: "𝔠", d: "𝔡", e: "𝔢", f: "𝔣", g: "𝔤", h: "𝔥", i: "𝔦", j: "𝔧",
  k: "𝔨", l: "𝔩", m: "𝔪", n: "𝔫", o: "𝔬", p: "𝔭", q: "𝔮", r: "𝔯", s: "𝔰", t: "𝔱",
  u: "𝔲", v: "𝔳", w: "𝔴", x: "𝔵", y: "𝔶", z: "𝔷",
};

const ACCENT = {
  hat: (s) => s + "\u0302", bar: (s) => s + "\u0304", overline: (s) => s + "\u0305",
  vec: (s) => s + "\u20D7", tilde: (s) => s + "\u0303", dot: (s) => s + "\u0307",
  ddot: (s) => s + "\u0308", check: (s) => s + "\u030C", breve: (s) => s + "\u0306",
  acute: (s) => s + "\u0301", grave: (s) => s + "\u0300",
};

// Pull a single balanced {...} group starting at position `i` (which must point AT `{`).
// Returns [innerContents, indexAfterClosingBrace]. If unbalanced returns null.
const grabGroup = (src, i) => {
  if (src[i] !== "{") return null;
  let depth = 0;
  for (let k = i; k < src.length; k++) {
    if (src[k] === "{") depth++;
    else if (src[k] === "}") {
      depth--;
      if (depth === 0) return [src.slice(i + 1, k), k + 1];
    }
  }
  return null;
};

// Pull a single argument starting at position `i` — may be a {...} group OR a single char/command
const grabArg = (src, i) => {
  if (src[i] === "{") return grabGroup(src, i);
  if (src[i] === "\\") {
    // backslash command — grab up to the next non-letter
    let k = i + 1;
    while (k < src.length && /[a-zA-Z]/.test(src[k])) k++;
    return [src.slice(i, k), k];
  }
  // single character
  return [src[i], i + 1];
};

export function latexToUnicode(input, opts = {}) {
  if (!input) return "";
  // When `forImage` is true, fall-back super/subscripts emit `^(...)` and
  // `_(...)` markers so the SVG renderer can stack them visually. The default
  // (forImage = false) emits clean inline chars — readable Unicode plaintext.
  const forImage = !!opts.forImage;
  let src = input;

  // Strip $...$ and \(...\) display delimiters
  src = src.replace(/\\\(|\\\)/g, "").replace(/\$+/g, "");
  // Strip \displaystyle / \textstyle / \scriptstyle / \,/\:/\;/\!
  src = src.replace(/\\(?:displaystyle|textstyle|scriptstyle)\b/g, "");
  src = src.replace(/\\[,:;!]/g, "");
  // LaTeX spacing commands → regular spaces
  src = src.replace(/\\(?:quad|qquad|enspace|thinspace|medspace|thickspace)\b\s?/g, "  ");
  // \\ inside text (line break) → space
  src = src.replace(/\\\\/g, " ");
  // \left( -> ( and \right) -> ) etc.
  src = src.replace(/\\left([\(\[\|\\{<.])/g, (_, c) => (c === "\\{" ? "{" : c === "." ? "" : c));
  src = src.replace(/\\right([\)\]\|\\{>.])/g, (_, c) => (c === "\\}" ? "}" : c === "." ? "" : c));
  // \begin{...}/\end{...} of unsupported envs — leave as placeholders
  src = src.replace(/\\begin\{(\w+)\}([\s\S]*?)\\end\{\1\}/g, (m, env, body) =>
    `[${env}: ${body.replace(/\\\\/g, " | ").trim()}]`
  );

  let out = "";
  let i = 0;
  while (i < src.length) {
    const c = src[i];

    if (c === "\\") {
      // Read command name
      let k = i + 1;
      while (k < src.length && /[a-zA-Z]/.test(src[k])) k++;
      const cmd = src.slice(i + 1, k);

      // ===== Greek =====
      if (GREEK[cmd]) { out += GREEK[cmd]; i = k; if (src[i] === " ") i++; continue; }
      // ===== Symbols =====
      if (SYMBOL[cmd]) { out += SYMBOL[cmd]; i = k; if (src[i] === " ") i++; continue; }
      // ===== \frac / \dfrac / \tfrac =====
      if (cmd === "frac" || cmd === "dfrac" || cmd === "tfrac") {
        const a = grabArg(src, k); if (!a) { out += "\\" + cmd; i = k; continue; }
        const b = grabArg(src, a[1]); if (!b) { out += "\\" + cmd; i = k; continue; }
        const num = latexToUnicode(a[0], opts); const den = latexToUnicode(b[0], opts);
        // Try unicode pre-built fractions for simple integer/integer
        const pretty = {
          "1/2": "½", "1/3": "⅓", "1/4": "¼", "1/5": "⅕", "1/6": "⅙", "1/7": "⅐",
          "1/8": "⅛", "1/9": "⅑", "1/10": "⅒", "2/3": "⅔", "3/4": "¾",
          "2/5": "⅖", "3/5": "⅗", "4/5": "⅘", "5/6": "⅚", "3/8": "⅜",
          "5/8": "⅝", "7/8": "⅞",
        }[`${num}/${den}`];
        out += pretty || `(${num})⁄(${den})`;
        i = b[1]; continue;
      }
      // ===== \sqrt[n]{x} or \sqrt{x} =====
      if (cmd === "sqrt") {
        let j = k;
        let n = null;
        if (src[j] === "[") {
          const close = src.indexOf("]", j);
          if (close > -1) { n = latexToUnicode(src.slice(j + 1, close), opts); j = close + 1; }
        }
        const a = grabArg(src, j); if (!a) { out += "√"; i = k; continue; }
        const inner = latexToUnicode(a[0], opts);
        out += n ? `${tryUnicodeRun(n, SUP) || `(${n})`}√(${inner})` : `√(${inner})`;
        i = a[1]; continue;
      }
      // ===== \sum_{a}^{b}, \int_{a}^{b}, \prod_{a}^{b}, \lim_{x \to a} =====
      // Handled by the symbol map + general subscript/superscript code below.
      // ===== \mathbb / \mathcal / \mathbf / \mathrm / \mathfrak =====
      if (cmd === "mathbb" || cmd === "mathcal" || cmd === "mathscr" || cmd === "mathbf" || cmd === "mathrm" || cmd === "mathfrak" || cmd === "mathit" || cmd === "mathsf" || cmd === "mathtt" || cmd === "text" || cmd === "operatorname") {
        const a = grabArg(src, k); if (!a) { out += "\\" + cmd; i = k; continue; }
        const inner = a[0];
        if (cmd === "mathbb") {
          out += [...inner].map((ch) => MATHBB[ch] || ch).join("");
        } else if (cmd === "mathcal" || cmd === "mathscr") {
          out += [...inner].map((ch) => MATHCAL[ch] || ch).join("");
        } else if (cmd === "mathfrak") {
          out += [...inner].map((ch) => MATHFRAK[ch] || ch).join("");
        } else {
          out += latexToUnicode(inner, opts); // \mathbf etc — fall back to plain text
        }
        i = a[1]; continue;
      }
      // ===== Accents =====
      if (ACCENT[cmd]) {
        const a = grabArg(src, k); if (!a) { out += "\\" + cmd; i = k; continue; }
        const inner = latexToUnicode(a[0], opts);
        // Apply the combining mark to each char of the result (for multi-char inputs, only first)
        const first = inner[0] || " ";
        out += ACCENT[cmd](first) + inner.slice(1);
        i = a[1]; continue;
      }
      // ===== Unknown command — keep verbatim as fallback =====
      out += "\\" + cmd;
      i = k;
      // eat one trailing space (LaTeX convention)
      if (src[i] === " ") i++;
      continue;
    }

    // ===== ^{...} and ^x — superscript =====
    if (c === "^") {
      const a = grabArg(src, i + 1);
      if (a) {
        const inner = latexToUnicode(a[0], opts);
        const ufy = tryUnicodeRun(inner, SUP);
        if (ufy !== null) {
          out += ufy;
        } else {
          // Image source keeps explicit markers so SVG renders true superscript.
          // Plain-text Unicode mode just inlines for clean readability.
          out += forImage ? `^(${inner})` : inner;
        }
        i = a[1]; continue;
      }
    }
    // ===== _{...} and _x — subscript =====
    if (c === "_") {
      const a = grabArg(src, i + 1);
      if (a) {
        const inner = latexToUnicode(a[0], opts);
        const ufy = tryUnicodeRun(inner, SUB);
        if (ufy !== null) {
          out += ufy;
        } else {
          out += forImage ? `_(${inner})` : inner;
        }
        i = a[1]; continue;
      }
    }

    // ===== Raw LaTeX grouping {...} — braces are invisible grouping in math,
    // so strip them and recurse into the contents. Prevents tensor / mathbb /
    // generic group output from leaking literal "{R}" style brackets into the
    // copyable Unicode text.
    if (c === "{") {
      const g = grabGroup(src, i);
      if (g) {
        out += latexToUnicode(g[0], opts);
        i = g[1];
        continue;
      }
      // Unbalanced opener — skip silently rather than echo it
      i++;
      continue;
    }
    // Stray closing brace — also drop (matched closers are consumed by grabGroup)
    if (c === "}") {
      i++;
      continue;
    }

    // Default: pass through
    out += c;
    i++;
  }

  // Tidy: collapse double spaces, trim
  return out.replace(/\s+/g, " ").trim();
}
